import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTaskStore, Task } from '../../../store/taskStore';
import { useProjectStore } from '../../../store/projectStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/Dialog';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';

const COLUMNS: { id: Task['status']; title: string; color: string }[] = [
  { id: 'backlog', title: 'Backlog', color: 'bg-card' },
  { id: 'todo', title: 'To Do', color: 'bg-info/20' },
  { id: 'in-progress', title: 'In Progress', color: 'bg-warning/20' },
  { id: 'review', title: 'Review', color: 'bg-primary/20' },
  { id: 'done', title: 'Done', color: 'bg-success/20' },
];

interface TaskBoardProps {
  isEmbedded?: boolean;
  projectId?: string;
}

export function TaskBoard({ isEmbedded, projectId }: TaskBoardProps) {
  const { tasks, fetchTasks, updateTaskStatus, createTask, isLoading, error } = useTaskStore();
  const { projects, fetchProjects } = useProjectStore();
  const [selectedProject, setSelectedProject] = useState<string>(projectId || '');
  const [isCreating, setIsCreating] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium' as Task['priority'],
    status: 'backlog' as Task['status'],
    dueDate: '',
    tags: '',
  });

  useEffect(() => {
    fetchTasks(selectedProject || projectId || '');
  }, [selectedProject, projectId]);

  useEffect(() => {
    if (!isEmbedded && projects.length === 0) {
      fetchProjects();
    }
  }, [isEmbedded, projects.length, fetchProjects]);

  const getTasksByStatus = (status: Task['status']) => {
    return tasks.filter((task) => task.status === status);
  };

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('taskId', taskId);
  };

  const handleDrop = async (e: React.DragEvent, status: Task['status']) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId) {
      await updateTaskStatus(taskId, status);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const priorityColors: Record<string, string> = {
    urgent: 'bg-destructive/10 text-destructive border-destructive/20',
    high: 'bg-warning/10 text-warning border-warning/20',
    medium: 'bg-info/10 text-info border-info/20',
    low: 'bg-card text-muted-foreground border-border',
  };

  if (isLoading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading agile board...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? '' : 'space-y-6'}>
      {!isEmbedded && (
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="tracking-tight text-foreground font-display text-page-title">Task <span className="text-foreground">Board</span></h1>
            <p className="text-muted-foreground mt-1">Manage and track agile work items</p>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <Button variant="unstyled" size="unstyled"
              onClick={() => setIsCreating(true)}
              className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all shadow-lg shadow-primary/20"
            >
              + New Task
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-semibold">
          {error}
        </div>
      )}

      <div className="flex gap-6 overflow-x-auto pb-6 scrollbar-hide">
        {COLUMNS.map((column) => {
          const columnTasks = getTasksByStatus(column.id);
          return (
            <div
              key={column.id}
              className="flex-shrink-0 w-80 flex flex-col"
              onDrop={(e) => handleDrop(e, column.id)}
              onDragOver={handleDragOver}
            >
              <div className="flex justify-between items-center px-4 py-3 mb-4 bg-card border border-border rounded-lg ring-1 ring-white/5 shadow-lg">
                <h3 className="text-foreground font-display text-card-title">{column.title}</h3>
                <span className="px-2 py-0.5 bg-background border border-border rounded-lg text-xs font-semibold text-muted-foreground">
                  {columnTasks.length}
                </span>
              </div>

              <div className="flex-1 space-y-4 min-h-[60vh] p-2 rounded-lg border-2 border-dashed border-border/50 group-hover:border-primary/20 transition-all">
                {columnTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    priorityColors={priorityColors}
                    onDragStart={handleDragStart}
                  />
                ))}
                {columnTasks.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <span className="text-2xl mb-2 opacity-20"></span>
                    <p className="text-xs font-semibold">Empty Stage</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={isCreating && !isEmbedded} onOpenChange={setIsCreating}>
        <DialogContent className="max-w-lg">
            <DialogHeader>
                <DialogTitle>Create Task</DialogTitle>
                <DialogDescription>
                  {selectedProject ? 'Linked to selected project' : 'No project selected'}
                </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-muted-foreground font-semibold mb-2">Title *</label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask((p) => ({ ...p, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 font-semibold"
                  placeholder="e.g., Setup CI pipeline"
                />
              </div>

              <div>
                <label className="block text-xs text-muted-foreground font-semibold mb-2">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask((p) => ({ ...p, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  rows={4}
                  placeholder="What should be done?"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-muted-foreground font-semibold mb-2">Stage</label>
                  <select
                    value={newTask.status}
                    onChange={(e) => setNewTask((p) => ({ ...p, status: e.target.value as any }))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 font-semibold"
                  >
                    <option value="backlog">Backlog</option>
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="review">Review</option>
                    <option value="done">Done</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground font-semibold mb-2">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask((p) => ({ ...p, priority: e.target.value as any }))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 font-semibold"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-muted-foreground font-semibold mb-2">Due Date</label>
                  <Input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask((p) => ({ ...p, dueDate: e.target.value }))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground font-semibold mb-2">Tags</label>
                  <Input
                    value={newTask.tags}
                    onChange={(e) => setNewTask((p) => ({ ...p, tags: e.target.value }))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 font-semibold"
                    placeholder="comma,separated,tags"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="unstyled" size="unstyled"
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 rounded-lg bg-background border border-border text-foreground font-semibold hover:bg-card transition-colors"
                >
                  Cancel
                </Button>
                <Button variant="unstyled" size="unstyled"
                  onClick={async () => {
                    if (!newTask.title.trim()) {
                      toast.error('Title is required.');
                      return;
                    }

                    try {
                      const payload: any = {
                        title: newTask.title.trim(),
                        description: newTask.description.trim() || undefined,
                        status: newTask.status,
                        priority: newTask.priority,
                        dueDate: newTask.dueDate || undefined,
                        tags: newTask.tags
                          ? newTask.tags.split(',').map((t) => t.trim()).filter(Boolean)
                          : [],
                        projectId: selectedProject || undefined,
                      };

                      await createTask(payload);
                      toast.success('Task created.');
                      setIsCreating(false);
                      setNewTask({
                        title: '',
                        description: '',
                        priority: 'medium',
                        status: 'backlog',
                        dueDate: '',
                        tags: '',
                      });
                      fetchTasks(selectedProject || projectId || '');
                    } catch (e: any) {
                      toast.error(e?.response?.data?.message || 'Failed to create task');
                    }
                  }}
                  className="px-5 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold transition-colors shadow-lg shadow-primary/20"
                >
                  Create
                </Button>
              </div>
            </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface TaskCardProps {
  task: Task;
  priorityColors: Record<string, string>;
  onDragStart: (e: React.DragEvent, taskId: string) => void;
}
function TaskCard({ task, priorityColors, onDragStart }: TaskCardProps) {
  const navigate = useNavigate();
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
      className="bg-card rounded-lg p-5 border border-border hover:border-primary/50 cursor-pointer active:cursor-grabbing hover:-translate-y-1 transition-all duration-300 group shadow-xl ring-1 ring-white/5"
    >
      <div className="flex justify-between items-start mb-3">
        <span className={`text-xs px-2 py-0.5 rounded-lg border font-semibold ${priorityColors[task.priority]}`}>
          {task.priority}
        </span>
        <div className="flex -space-x-2 items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-card border border-border flex items-center justify-center text-xs font-semibold text-muted-foreground">
            {task.assigneeName?.[0] || '?'}
          </div>
          <select 
            className="text-xs border border-border rounded px-1 py-0.5 bg-background text-foreground hover:bg-card focus:outline-none focus:ring-1 focus:ring-primary"
            value={task.status}
            onChange={(e) => {
              e.stopPropagation();
              useTaskStore.getState().updateTaskStatus(task.id, e.target.value as any);
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="backlog">Backlog</option>
            <option value="todo">To Do</option>
            <option value="in-progress">In Progress</option>
            <option value="review">Review</option>
            <option value="done">Done</option>
          </select>
        </div>
      </div>

      <h4 
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/work/tasks/${task.id}`);
        }}
        className="text-foreground font-semibold text-sm leading-tight mb-3 hover:text-primary transition-colors hover:underline"
      >
        {task.title}
      </h4>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {task.tags?.map(tag => (
          <span key={tag} className="text-xs font-semibold text-muted-foreground hover:text-muted-foreground cursor-pointer">#{tag}</span>
        ))}
      </div>

      <div className="flex justify-between items-center pt-3 border-t border-border/50">
        <div className="flex items-center gap-3 text-muted-foreground">
          {task.subtasks?.total > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs"></span>
              <span className="text-xs font-semibold">{task.subtasks.completed}/{task.subtasks.total}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <span className="text-xs"></span>
            <span className="text-xs font-semibold">{task.commentsCount || 0}</span>
          </div>
        </div>
        {task.storyPoints && (
          <div className="w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center text-xs font-semibold text-primary">
            {task.storyPoints}
          </div>
        )}
      </div>
    </div>
  );
}
